// @ts-nocheck
import {HitComponentsVuePlugin} from '@hit/components';
import {createApp} from 'vue';
import {createPinia} from 'pinia';
import router from './router';
import App from './App.vue';
import './assets/tailwind.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Keycloak from 'keycloak-js';
import {globals} from './globals.js';
import {globals as appGlobals} from '../../app/src/globals.js';
import {setExternalI18n} from '@hit/base/src/plugins/i18n/i18n';
import {i18n} from './plugins/i18n';
import {
  getStoredKeycloakTokens,
  refreshTokenOrRecover,
  registerKeycloakForTokenSync,
  startTokenRefreshLoop,
  storeKeycloakTokens,
} from '@/auth/keycloak';

const keycloakConfig = {
  url: process.env.VUE_APP_KEYCLOAK_AUTH_URL,
  realm: process.env.VUE_APP_KEYCLOAK_REALM,
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
};

const keycloak = new Keycloak(keycloakConfig);

function buildAppContext(kc) {
  const idToken = kc.idTokenParsed || {};
  const token = kc.tokenParsed || {};
  return {
    companyId: idToken.company_id || token.company_id,
    companyName: token.company_name || idToken.company_name || '',
    postgrestUrl: process.env.VUE_APP_POSTGREST_URL,
    // Optional override to force PostgREST profile independently from JWT schema_id.
    postgrestProfile:
      process.env.VUE_APP_POSTGREST_PROFILE ||
      token.schema_id ||
      idToken.schema_id,
    reportUrl: process.env.VUE_APP_REPORT_URL,
    storageUrl: process.env.VUE_APP_STORAGE_URL,
    actionUrl: process.env.VUE_APP_ACTION_URL,
    notifyUrl: process.env.VUE_APP_NOTIFY_URL,
    adminApiUrl: process.env.VUE_APP_ADMIN_API_URL,
    adminUiUrl: process.env.VUE_APP_ADMIN_UI_URL,
    user: {
      id: idToken.sub || token.sub,
      firstName: idToken.given_name || token.given_name,
      lastName: idToken.family_name || token.family_name,
    },
  };
}

function setupGlobals(kc) {
  const appContext = buildAppContext(kc);

  globals.$keycloak = kc;
  globals.$i18n = i18n;
  globals.$appContext = appContext;

  // hit-base services (BaseService, DataService, HitContainer) read app globals.
  appGlobals.$keycloak = kc;
  appGlobals.$appContext = appContext;
}

async function ensureValidToken() {
  try {
    await keycloak.updateToken(60);
    storeKeycloakTokens(keycloak);
    return true;
  } catch {
    const outcome = await refreshTokenOrRecover(keycloak, 60, {
      loginOnFailure: true,
      forcePromptLogin: false,
    });
    if (outcome.ok) {
      storeKeycloakTokens(keycloak);
    }
    return outcome.ok;
  }
}

registerKeycloakForTokenSync(keycloak);

keycloak.onAuthSuccess = () => {
  storeKeycloakTokens(keycloak);
};

keycloak.onTokenExpired = () => {
  void refreshTokenOrRecover(keycloak, 70, {
    loginOnFailure: true,
    forcePromptLogin: false,
  });
};

keycloak
  .init({
    checkLoginIframe: false,
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri:
      window.location.origin + '/silent-check-sso.html',
    pkceMethod: 'S256',
    ...getStoredKeycloakTokens(),
  })
  .then(async (authenticated) => {
    if (!authenticated) {
      keycloak.login({scope: 'openid'});
      return;
    }

    const tokenReady = await ensureValidToken();
    if (!tokenReady) {
      return;
    }

    setupGlobals(keycloak);
    setExternalI18n(i18n);
    storeKeycloakTokens(keycloak);

    startTokenRefreshLoop(keycloak, {
      intervalMs: 30_000,
      minValiditySeconds: 60,
    });

    const app = createApp(App);
    app.use(createPinia());
    app.use(HitComponentsVuePlugin);
    app.use(router);
    app.use(i18n);
    app.mount('#app');
  })
  .catch((error) => {
    console.error('Keycloak init failed:', error);
    keycloak.login({scope: 'openid'});
  });
